import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ForumIcon from '@mui/icons-material/Forum';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { lastRefreshUnreadMessageCountSelector } from '../../features/dialogSlice';
import { ResourceMini } from '../resources/ResourceMini';
import { FloatingContainerMorph } from './FloatingContainerMorph';

interface FloatingResourcesProps {
  authStatus: string;
  profile: any;
  fullWidth?: boolean;
  minimized?: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
}

export const FloatingResources: React.FC<FloatingResourcesProps> = ({
  fullWidth = false,
  minimized = false,
  onMinimize,
  onExpand,
  authStatus,
}) => {
  const navigate = useNavigate();
  const badgeMessageCount = useSelector(lastRefreshUnreadMessageCountSelector);
  const [showNewsDialog, setShowNewsDialog] = useState(false);

  return (
    <>
      <FloatingContainerMorph
        minimized={minimized}
        onMinimize={onMinimize}
        onExpand={onExpand}
        icon={<LinkIcon sx={{ color: '#656EFF', fontSize: 22 }} />}
        title="Quick Links"
        sx={{ width: fullWidth ? '100%' : 260 }}
      >
        {/* Knowledge Connect */}
        <ListItemButton
          onClick={() => navigate('/Resources')}
          sx={{ py: 0.75, px: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <MenuBookIcon sx={{ color: '#656EFF', fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Knowledge Connect"
            primaryTypographyProps={{ fontSize: 14, color: '#333' }}
          />
        </ListItemButton>

        {/* Conversations with unread badge */}
        <ListItemButton
          onClick={() => navigate('/Conversations')}
          sx={{ py: 0.75, px: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Badge
              badgeContent={badgeMessageCount}
              color="error"
              invisible={!badgeMessageCount || authStatus !== 'authenticated'}
              showZero={false}
              overlap="circular"
              sx={{ '& .MuiBadge-badge': { top: -2, right: -4, fontSize: 10, minWidth: 16, height: 16 } }}
            >
              <ForumIcon sx={{ color: '#F5A623', fontSize: 20 }} />
            </Badge>
          </ListItemIcon>
          <ListItemText
            primary="Conversations"
            primaryTypographyProps={{ fontSize: 14, color: '#333' }}
          />
        </ListItemButton>

        {/* Latest News */}
        <ListItemButton
          onClick={() => setShowNewsDialog(true)}
          sx={{ py: 0.75, px: 2 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <NewspaperIcon sx={{ color: '#E57399', fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Latest News"
            primaryTypographyProps={{ fontSize: 14, color: '#333' }}
          />
        </ListItemButton>

        <Box sx={{ height: 8 }} />
      </FloatingContainerMorph>

      {/* Latest News Dialog */}
      <Dialog
        onClose={() => setShowNewsDialog(false)}
        open={showNewsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.82)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(164, 142, 233, 0.18)',
            boxShadow: '0 8px 32px rgba(80, 60, 160, 0.12)',
            borderRadius: 3,
            maxHeight: '80vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ px: { xs: 1.5, sm: 3 }, py: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" gap={1}>
              <NewspaperIcon sx={{ color: '#656EFF' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Latest News</Typography>
            </Stack>
            <IconButton aria-label="close" onClick={() => setShowNewsDialog(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 1.5, sm: 2 }, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ResourceMini hideTitle />
        </Box>
      </Dialog>
    </>
  );
};
